import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { CRMIntegrationService } from '@obis/integration';

interface FeeStructure {
  class: string;
  monthlyFee: number;
  admissionFeeRange?: {
    minimum: number;
    default: number;
    maximum: number;
  };
  maxDiscountAllowed: number;
  uniformFee: number;
  booksAndCopies: number;
  examFee: number;
  appFee: number;
  transportFee: number;
  libraryFee: number;
  activityFee: number;
}

const EventRegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    studentName: '',
    studentAge: '',
    currentGrade: '',
    parentPhone: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [feeStructure, setFeeStructure] = useState<FeeStructure | null>(null);
  const [feeStructureLoading, setFeeStructureLoading] = useState(false);

  const ages = Array.from({ length: 11 }, (_, i) => (4 + i).toString());
  
  const grades = [
    'No Class', 'NUR', 'JR. KG', 'SR. KG', 'I', 'II', 'III', 'IV', 'V', 'VI',
    'VII', 'VIII', 'IX', 'X', 'XI', 'XII'
  ];

  // Fetch fee structure when class or gender changes
  useEffect(() => {
    const loadFeeStructure = async () => {
      if (!formData.currentGrade || formData.currentGrade === 'No Class' || !formData.gender) {
        setFeeStructure(null);
        return;
      }

      setFeeStructureLoading(true);
      try {
        const structure = await CRMIntegrationService.getFeeStructureFromERP(formData.currentGrade, formData.gender);
        setFeeStructure(structure);
      } catch (err) {
        console.error('Error loading fee structure:', err);
        setFeeStructure(null);
      } finally {
        setFeeStructureLoading(false);
      }
    };

    loadFeeStructure();
  }, [formData.currentGrade, formData.gender]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create lead with event registration data
      const response = await fetch(`${API_BASE_URL}/public/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          parentName: formData.parentName,
          studentName: formData.studentName,
          studentAge: parseInt(formData.studentAge),
          currentSchool: '', // Optional for event registration
          preferredGrade: formData.currentGrade,
          inquirySource: 'event', // Event registration
          parentPhone: formData.parentPhone,
          parentEmail: '', // Optional
          branchId: 'kolkata',
          // Auto-filled for event leads
          priority: 'warm',
          eventName: 'Drawing Competition 2026'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to register');
      }

      setSuccess(true);
      setFormData({
        parentName: '',
        studentName: '',
        studentAge: '',
        currentGrade: '',
        parentPhone: '',
        gender: ''
      });

      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <img 
            src="/olive-bloom-logo.png" 
            alt="Olive Bloom International School" 
            className="h-20 w-20 mx-auto mb-4 object-contain"
            onError={(e) => {
              // Fallback if image doesn't load
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful! 🎉</h2>
          <p className="text-gray-600 mb-4">
            Thank you for registering for the Drawing Competition!
          </p>
          <p className="text-sm text-gray-500 mb-6">
            We'll contact you soon with more details. Get ready to showcase your artistic skills!
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            Register Another Student
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <img 
            src="/olive-bloom-logo.png" 
            alt="Olive Bloom International School" 
            className="h-24 w-24 mx-auto mb-4 object-contain"
            onError={(e) => {
              // Fallback if image doesn't load
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <h2 className="text-xl font-extrabold text-blue-900 tracking-wide mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
            OLIVE BLOOM INT. SCHOOL
          </h2>
          
          {/* Event Header */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">🎨 Drawing Competition</h1>
          <p className="text-gray-600 font-medium">Register your child for the event</p>
          <p className="text-sm font-bold text-blue-600 bg-blue-50 px-4 py-3 rounded-lg mt-3 border-2 border-blue-600 whitespace-nowrap overflow-hidden text-ellipsis">
            Sun, Jan 11, 2026 • 12:00 PM - 3:00 PM
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Parent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Name *
            </label>
            <input
              type="text"
              required
              value={formData.parentName}
              onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your name"
            />
          </div>

          {/* Student Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Student Name *
            </label>
            <input
              type="text"
              required
              value={formData.studentName}
              onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Child's name"
            />
          </div>

          {/* Student Age */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Student Age *
            </label>
            <select
              required
              value={formData.studentAge}
              onChange={(e) => setFormData({ ...formData, studentAge: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select age</option>
              {ages.map(age => (
                <option key={age} value={age}>{age} years</option>
              ))}
            </select>
          </div>

          {/* Current Grade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Grade *
            </label>
            <select
              required
              value={formData.currentGrade}
              onChange={(e) => setFormData({ ...formData, currentGrade: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select grade</option>
              {grades.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender
            </label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select gender (optional)</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Fee Structure Display */}
          {feeStructureLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">Loading fee structure...</p>
            </div>
          )}

          {feeStructure && !feeStructureLoading && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-3">Fee Structure for {formData.currentGrade}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Monthly Fee:</span>
                  <span className="font-semibold">₹{feeStructure.monthlyFee}</span>
                </div>
                {feeStructure.admissionFeeRange && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Admission Fee (Min):</span>
                      <span className="font-semibold">₹{feeStructure.admissionFeeRange.minimum}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Admission Fee (Default):</span>
                      <span className="font-semibold">₹{feeStructure.admissionFeeRange.default}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-700">Uniform Fee:</span>
                  <span className="font-semibold">₹{feeStructure.uniformFee}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Books & Copies:</span>
                  <span className="font-semibold">₹{feeStructure.booksAndCopies}</span>
                </div>
                <div className="border-t border-green-200 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">Max Discount Allowed:</span>
                  <span className="font-bold text-green-600">₹{feeStructure.maxDiscountAllowed}</span>
                </div>
              </div>
            </div>
          )}

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.parentPhone}
              onChange={(e) => {
                let value = e.target.value.replace(/[^\d+]/g, '');
                if (!value.startsWith('+91')) {
                  if (value.startsWith('91')) {
                    value = '+' + value;
                  } else if (value.startsWith('+')) {
                    value = '+91';
                  } else if (value.length > 0) {
                    value = '+91' + value;
                  }
                }
                if (value.length > 13) {
                  value = value.substring(0, 13);
                }
                setFormData({ ...formData, parentPhone: value });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+91 98765 43210"
            />
            <p className="text-xs text-gray-500 mt-1">Format: +91XXXXXXXXXX</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {loading ? 'Registering...' : 'Register Now'}
          </button>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-900">
              ✓ Free Entry<br/>
              ✓ Prizes & Certificates<br/>
              ✓ Admission Discount Voucher<br/>
              ✓ School Tour Available
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventRegistrationForm;
